import { useLayoutEffect, useReducer } from 'react';
import { CircleCheck, Trophy, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMyCpmWalletQuery } from '@/generated/graphql';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCents, formatExactCents } from '@/lib/format/currency';
import { ACCOUNT_CHANGED_EVENT } from '@/lib/axios';
import { summarizeEndedCampaigns, type EndedCampaignDistribution } from '@/lib/campaigns/summarizeEndedCampaigns';

function MyEarningsSummaryCard() {
  const [sessionRevision, resetSession] = useReducer(revision => revision + 1, 0);

  useLayoutEffect(() => {
    window.addEventListener(ACCOUNT_CHANGED_EVENT, resetSession);
    return () => window.removeEventListener(ACCOUNT_CHANGED_EVENT, resetSession);
  }, []);

  return <MyEarningsSummarySnapshot key={sessionRevision} />;
}

function MyEarningsSummarySnapshot() {
  const { t } = useTranslation();
  // Keep each session's wallet read independent: old cached data and delayed
  // requests must never become another account's lifetime earnings.
  const { data, loading, error } = useMyCpmWalletQuery({
    fetchPolicy: 'no-cache',
    context: { queryDeduplication: false },
  });
  const wallet = error ? undefined : data?.myCpmWallet;
  const isLoading = loading && !wallet;

  return (
    <div className="min-w-0 rounded-xl border border-border/50 bg-card/60 p-3" data-testid="ended-campaigns-my-earnings">
      <dt className="text-[10px] font-medium uppercase tracking-wider text-slate-400">{t('earnPage.endedSummary.myEarnings')}</dt>
      <dd className="mt-1" aria-busy={isLoading}>
        {isLoading ? (
          <Skeleton className="h-6 w-28" role="status" aria-label={t('common.loading')} />
        ) : (
          <p className="break-words text-xl font-extrabold leading-tight tabular-nums text-primary">
            {wallet ? formatCents(wallet.total_earned_cents, wallet.currency) : '—'}
          </p>
        )}
        <p className="mt-1 text-[10px] text-muted-foreground">
          {t(!isLoading && !wallet ? 'earnPage.endedSummary.earningsUnavailable' : 'earnPage.endedSummary.myEarningsNote')}
        </p>
      </dd>
    </div>
  );
}

export function EndedCampaignsSummary({ campaigns }: { campaigns: readonly EndedCampaignDistribution[] }) {
  const { t } = useTranslation();
  const summaries = summarizeEndedCampaigns(campaigns);

  // Rankings retain their own prizes/results without any claim of paid awards.
  if (summaries.length === 0) {
    return (
      <div>
        <h2 className="flex items-center gap-2 text-xl font-bold">
          <Trophy aria-hidden="true" className="h-5 w-5 text-muted-foreground" />
          {t('earnPage.endedCampaignsTitle')}
        </h2>
        <p className="mt-0.5 text-sm text-muted-foreground">{t('earnPage.endedCampaignsSubtitle')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="ended-campaigns-summary">
      <div className="flex flex-col gap-4 rounded-2xl border border-emerald-500/25 bg-gradient-to-r from-card via-slate-900/40 to-emerald-500/5 p-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-xl font-bold tracking-tight">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-amber-400/40 bg-amber-400/10">
              <Trophy aria-hidden="true" className="h-4 w-4 text-amber-400" />
            </span>
            {t('earnPage.endedCampaignsTitle')}
          </h2>
          <p className="mt-1 max-w-xl text-xs leading-5 text-slate-300">
            {t('earnPage.endedSummary.description')}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-3 rounded-xl border border-emerald-500/35 bg-black/40 px-3.5 py-3 shadow-[0_0_24px_rgba(16,185,129,0.08)]">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-emerald-500/40 bg-emerald-500/10">
            <Wallet aria-hidden="true" className="h-4 w-4 text-emerald-400" />
          </span>
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wider text-emerald-400">
              <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400/70" />
              {t('earnPage.endedSummary.totalPaid')}
            </p>
            {summaries.map(summary => (
              <p key={summary.currency} className="break-words text-2xl font-extrabold leading-tight tabular-nums text-emerald-400">
                {formatExactCents(summary.totalCents, summary.currency)}
              </p>
            ))}
            <p className="mt-1 flex items-center gap-1 text-[9px] text-slate-400">
              <CircleCheck aria-hidden="true" className="h-2.5 w-2.5 shrink-0 text-emerald-400" />
              {t('earnPage.endedSummary.walletNote')}
            </p>
          </div>
        </div>
      </div>

      <dl className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="min-w-0 rounded-xl border border-border/50 bg-card/60 p-3">
          <dt className="text-[10px] font-medium uppercase tracking-wider text-slate-400">{t('earnPage.endedSummary.totalDistributed')}</dt>
          {summaries.map(summary => (
            <dd key={summary.currency} className="mt-1">
              <p className="break-words text-xl font-extrabold leading-tight tabular-nums text-emerald-400">{formatExactCents(summary.totalCents, summary.currency)}</p>
              <p className="mt-1 text-[10px] text-muted-foreground">{t('earnPage.endedSummary.closedCycles', { count: summary.count })}</p>
            </dd>
          ))}
        </div>
        <div className="min-w-0 rounded-xl border border-border/50 bg-card/60 p-3">
          <dt className="text-[10px] font-medium uppercase tracking-wider text-slate-400">{t('earnPage.endedSummary.average')}</dt>
          {summaries.map(summary => (
            <dd key={summary.currency} className="mt-1">
              <p className="break-words text-xl font-extrabold leading-tight tabular-nums">{formatExactCents(summary.averageCents, summary.currency)}</p>
              <p className="mt-1 text-[10px] text-muted-foreground">{t('earnPage.endedSummary.perCycle')}</p>
            </dd>
          ))}
        </div>
        <div className="min-w-0 rounded-xl border border-border/50 bg-card/60 p-3">
          <dt className="text-[10px] font-medium uppercase tracking-wider text-slate-400">{t('earnPage.endedSummary.largest')}</dt>
          {summaries.map(summary => (
            <dd key={summary.currency} className="mt-1">
              <p className="break-words text-xl font-extrabold leading-tight tabular-nums text-amber-400">{formatExactCents(summary.largestCampaign.distributedCents, summary.currency)}</p>
              <p className="mt-1 break-words text-[10px] text-muted-foreground">{summary.largestCampaign.name}</p>
            </dd>
          ))}
        </div>
        <MyEarningsSummaryCard />
      </dl>
    </div>
  );
}
